/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag Fragment
 */
import { type ReactNode, useCallback, useEffect, useMemo, useState } from 'react';

import { css, jsx } from '@compiled/react';
import { FormattedMessage } from 'react-intl';
import { graphql, useFragment } from 'react-relay';

import { cssMap } from '@atlaskit/css';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box, Flex } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { GitbookIcon } from '@atlassian/rovo-connectors-config/icons';

import { TypeFilterValueKey } from '../../../../../../../common/constants/filters/universal-type';
import { ProductKeys } from '../../../../../../../common/constants/product-keys';
import type { ResultClickedCallbackParams } from '../../../../../../../common/constants/types';
import type { SearchGraphResultProps } from '../../../../../../../common/types';
import { getDocumentSubtypeLabel } from '../../../../../../../common/ui/3p-result-labeling';
import { getCommentCount } from '../../../../../../../common/ui/comment-count';
import { DescriptionBody } from '../../../../../../../common/ui/description-body';
import { type EmbeddedComment } from '../../../../../../../common/ui/embedded-comments';
import { LastModified } from '../../../../../../../common/ui/last-modified';
import { getLikeCount, LIKE_REACTION_TYPE } from '../../../../../../../common/ui/like-count';
import { SearchResult } from '../../../../../../../common/ui/search-result';
import { SearchResultContextFooter } from '../../../../../../../common/ui/search-result-context-footer';
import { SearchResultThumbnail } from '../../../../../../../common/ui/search-result-thumbnail';
import { StatusLozengeButton } from '../../../../../../../common/ui/status-lozenge-button';
import { isNonPublic } from '../../../../../../../common/utils/is-non-public';
import {
	enrichExcerpt,
	processExcerpt,
} from '../../../../../../../common/utils/search-result-utils';
import { toThirdPartyRestrictionLevel } from '../../../../../../../common/utils/to-third-party-restriction-level';
import { useSelectedFilters } from '../../../../../../../controllers/filters/selected';

import type { searchGraphDocument_AtlassianSearchPage$key } from './__generated__/searchGraphDocument_AtlassianSearchPage.graphql';

const contentIconStyles = css({
	width: token('space.400'),
	height: token('space.400'),
	paddingTop: token('space.025'),
	paddingRight: token('space.025'),
	paddingBottom: token('space.025'),
	paddingLeft: token('space.025'),
	boxSizing: 'border-box',
	objectFit: 'contain',
});

const styles = cssMap({
	documentContainer: {
		width: '100%',
	},
	resultWrapper: {
		flex: '1',
		minWidth: '0px',
	},
	labelContainer: {
		'&::first-letter': {
			textTransform: 'capitalize',
		},
	},
});

const IMAGE_THUMBNAIL_WIDTH = 204;
const IMAGE_THUMBNAIL_HEIGHT = 132;

const getSizedMediaThumbnailUrl = (mediaThumbnailUrl: string): string | null => {
	try {
		const thumbnailUrl = new URL(mediaThumbnailUrl);
		if (thumbnailUrl.protocol !== 'https:') {
			return null;
		}
		thumbnailUrl.searchParams.set('width', String(IMAGE_THUMBNAIL_WIDTH));
		thumbnailUrl.searchParams.set('height', String(IMAGE_THUMBNAIL_HEIGHT));
		return thumbnailUrl.toString();
	} catch {
		return null;
	}
};

export const SearchGraphDocument: ({
	node,
	position,
	cloudId,
	selectedProduct,
	primaryProduct,
	primarySubProduct,
	appMode,
	product,
	icon,
	onResultClicked,
	onResultContextMenu,
	onCopyLinkButtonClicked,
	onSummarizeInChatButtonClicked,
	onContextMenuCopyButtonClicked,
	onContextMenuButtonClicked,
	subtype,
	thirdPartyConfigs,
	mainContentWidth,
	selectedSites,
	is3pGroupingEnabled,
}: SearchGraphResultProps<searchGraphDocument_AtlassianSearchPage$key>) => JSX.Element = ({
	node,
	position,
	cloudId,
	selectedProduct,
	primaryProduct,
	primarySubProduct,
	appMode,
	product,
	icon,
	onResultClicked,
	onResultContextMenu,
	onCopyLinkButtonClicked,
	onSummarizeInChatButtonClicked,
	onContextMenuCopyButtonClicked,
	onContextMenuButtonClicked,
	subtype,
	thirdPartyConfigs,
	mainContentWidth,
	selectedSites,
	is3pGroupingEnabled,
}: SearchGraphResultProps<searchGraphDocument_AtlassianSearchPage$key>) => {
	const data = useFragment(
		graphql`
			fragment searchGraphDocument_AtlassianSearchPage on SearchResultGraphDocument
			@argumentDefinitions(
				includeThirdPartyId: { type: "Boolean!", defaultValue: true }
				includeReactionsAndCommentCount: { type: "Boolean", defaultValue: false }
			) {
				__typename
				id
				title
				url
				excerpt
				lastModifiedDate
				permissionLevel
				entity {
					... on ExternalDocument {
						thirdPartyId @include(if: $includeThirdPartyId)
						status
						type {
							iconUrl
						}
						nonIndexableContent @optIn(to: "ExternalDataDepotQueryV2") {
							mediaThumbnailUrl
						}
						parent @optIn(to: "ExternalDataDepotQueryV2") {
							... on ExternalDocument {
								displayName
							}
						}
						container @optIn(to: "ExternalDataDepotQueryV2") {
							... on ExternalSpace {
								displayName
								url
							}
						}
						reactions @include(if: $includeReactionsAndCommentCount) {
							reactionType
							total
						}
						commentCount @include(if: $includeReactionsAndCommentCount)
					}
				}
				linkedEntities {
					id
					parentId
					excerpt
					url
					entity {
						... on ExternalComment {
							displayName
						}
					}
				}
				linkedResults {
					id
					title
					url
					type
					description
					lastModifiedDate
					category
					subtype
					integrationId
					providerId
					connectorType
				}
			}
		`,
		node,
	);

	/**
	 * A wrapper to include thirdPartyId in the onResultClicked callback.
	 */
	const onResultClickedWithThirdPartyId = useCallback(
		(params: ResultClickedCallbackParams = {}) => {
			const thirdPartyId = data.entity?.thirdPartyId || undefined;
			return onResultClicked?.({ ...params, thirdPartyId });
		},
		[onResultClicked, data],
	);

	/**
	 * A wrapper to include thirdPartyId in the onResultContextMenu callback.
	 */
	const onResultContextMenuWithThirdPartyId = useCallback(
		(params: ResultClickedCallbackParams = {}) => {
			const thirdPartyId = data.entity?.thirdPartyId || undefined;
			return onResultContextMenu?.({ ...params, thirdPartyId });
		},
		[onResultContextMenu, data],
	);

	const { id, url, title, excerpt, entity, lastModifiedDate, permissionLevel, linkedEntities } =
		data;
	const isNotPublic = isNonPublic(permissionLevel);
	const parentName = entity?.parent?.displayName || null;
	const containerName = entity?.container?.displayName || null;

	// Status is surfaced in the top-right column for Salesforce knowledge articles,
	// gated behind the same feature flag as the Salesforce document status filter.
	const statusText =
		product === ProductKeys.Salesforce && fg('rovo-fps-3p-ux-salesforce-document-status-filter')
			? (entity?.status ?? '')
			: '';

	// Transform linkedEntities (comments) into EmbeddedComment format
	const embeddedComments: EmbeddedComment[] = useMemo(() => {
		if (!linkedEntities?.length) {
			return [];
		}
		return linkedEntities
			.filter((linkedEntity) => linkedEntity.excerpt)
			.map(
				(linkedEntity): EmbeddedComment => ({
					id: linkedEntity.id,
					parentId: linkedEntity.parentId ?? null,
					excerpt: linkedEntity.excerpt ?? null,
					url: linkedEntity.url,
					displayName: linkedEntity.entity?.displayName ?? null,
				}),
			);
	}, [linkedEntities]);

	// Memoized URL validation function to avoid recreation on every render
	const isValidIconUrl = useCallback((url: string): boolean => {
		try {
			const parsedUrl = new URL(url);
			// Only allow https URLs for security
			return parsedUrl.protocol === 'https:';
		} catch {
			return false;
		}
	}, []);

	// Get icon URL and validate it
	const iconUrl = entity?.type?.iconUrl;
	const validatedIconUrl = useMemo(() => {
		return iconUrl && isValidIconUrl(iconUrl) ? iconUrl : null;
	}, [iconUrl, isValidIconUrl]);

	// State to track if the icon image failed to load
	const [iconLoadError, setIconLoadError] = useState(false);

	// Handle icon load error
	const handleIconError = useCallback(() => {
		setIconLoadError(true);
	}, []);

	// Reset error state when iconUrl changes
	useEffect(() => {
		setIconLoadError(false);
	}, [validatedIconUrl]);

	// Memoize document icon creation to avoid recreation when props haven't changed
	const documentIcon = useMemo(() => {
		// GitBook support - use GitbookIcon when selectedProduct is 'gitbook'
		if (selectedProduct === 'gitbook') {
			return <GitbookIcon />;
		}

		if (!validatedIconUrl || iconLoadError) {
			return icon;
		}
		return (
			<img
				src={validatedIconUrl}
				css={contentIconStyles}
				onError={handleIconError}
				alt=""
				role="presentation"
			/>
		);
	}, [selectedProduct, validatedIconUrl, iconLoadError, handleIconError, icon]);

	const shouldShowLastModified = lastModifiedDate && product !== ProductKeys.AtlassianWebCrawler;
	const lastModifiedComponent = shouldShowLastModified ? (
		<LastModified lastModified={lastModifiedDate} />
	) : null;

	// We overwrite some subtype labels by product, eg for presentation->PowerPoint
	const label = getDocumentSubtypeLabel({ product, subtype });
	const subtypeComponent = subtype && (
		<Box xcss={styles.labelContainer}>{subtype?.replace(/-/g, ' ')}</Box>
	);
	const labelComponent = label && (
		<Box xcss={styles.labelContainer}>
			<FormattedMessage {...label} />
		</Box>
	);

	const headerMetadataParts: [string, ReactNode][] = [
		['label', labelComponent || subtypeComponent || null],
		['containerName', containerName],
		['parentName', parentName || null],
		['lastModified', lastModifiedComponent],
	];

	const likeCount = useMemo(() => {
		if (!fg('rovo_fps_graph_like_comment_count')) {
			return null;
		}
		const numberOfLikes =
			entity?.reactions?.find(
				(reaction) => reaction?.reactionType?.toLowerCase() === LIKE_REACTION_TYPE,
			)?.total ?? 0;
		return getLikeCount(numberOfLikes);
	}, [entity?.reactions]);

	const commentCount = useMemo(() => {
		if (!fg('rovo_fps_graph_like_comment_count')) {
			return null;
		}
		const numberOfComments = entity?.commentCount ?? 0;
		return getCommentCount(numberOfComments);
	}, [entity?.commentCount]);

	const headerMetadataSocialParts: [string, ReactNode][] = [
		['like-count', likeCount],
		['comment-count', commentCount],
	];

	const enrichedTitle = enrichExcerpt(title);

	const mediaThumbnailUrl = entity?.nonIndexableContent?.mediaThumbnailUrl;
	const sizedMediaThumbnailUrl = mediaThumbnailUrl
		? getSizedMediaThumbnailUrl(mediaThumbnailUrl)
		: null;
	const [thumbnailLoadError, setThumbnailLoadError] = useState(false);
	const shouldAttemptThumbnail = !!(subtype === 'image' && sizedMediaThumbnailUrl);

	const description =
		shouldAttemptThumbnail && !thumbnailLoadError ? (
			<SearchResultThumbnail
				resultUrl={url}
				thumbnailUrl={sizedMediaThumbnailUrl}
				onError={() => setThumbnailLoadError(true)}
			/>
		) : (
			excerpt && <DescriptionBody description={processExcerpt(excerpt)} />
		);
	const linkedResults = data.linkedResults?.map((linkedResult) => ({
		id: linkedResult.id,
		title: linkedResult.title,
		url: linkedResult.url,
		type: linkedResult.type,
		description: linkedResult.description,
		lastModifiedDate: linkedResult.lastModifiedDate,
		category: linkedResult.category,
		subtype: linkedResult.subtype,
		integrationId: linkedResult.integrationId,
		providerId: linkedResult.providerId,
		connectorType: linkedResult.connectorType,
	}));

	// When the user explicitly selects the "Comments" type filter, treat that as an opt-in for
	// embedded comments — surface (and auto-expand) the Comments tab even if the per-product
	// rollout flag is off. This keeps the filtered view's intent consistent with what's shown.
	const { selectedEntities } = useSelectedFilters({});
	const isCommentsFilterActive = selectedEntities.includes(TypeFilterValueKey.Comment);

	// Embedded comments are gated per product so each product can be toggled independently:
	//   - Google Drive: enable_3p_embedded_comments_search
	//   - SharePoint:   rovo_fps_sharepoint_embedded_comments
	// Other products fall back to the shared 3p flag (current behaviour).
	// The Comments filter selection bypasses the per-product gate (the user has explicitly
	// asked for comments — honour that intent).
	const isEmbeddedCommentsEnabled =
		isCommentsFilterActive ||
		(product === ProductKeys.Sharepoint
			? fg('rovo_fps_sharepoint_embedded_comments')
			: fg('enable_3p_embedded_comments_search'));

	// Mentioned-in is scoped to Google Drive and SharePoint results.
	const isDriveOrSharepointResult =
		product === ProductKeys.GoogleDrive || product === ProductKeys.Sharepoint;
	const isMentionedInEnabled = isDriveOrSharepointResult;

	// SearchResultContextFooter renders Comments / Mentioned in / Related links as peer expand
	// buttons in a single shared ExpandList. By product convention only one "linked" surface is
	// shown at a time: Related links takes priority over Mentioned in when SIMILAR items exist.
	// All rows start collapsed and the user expands one at a time.
	const footerComponent = (
		<SearchResultContextFooter
			embeddedComments={embeddedComments}
			isEmbeddedCommentsEnabled={isEmbeddedCommentsEnabled}
			forceCommentsExpanded={isCommentsFilterActive}
			linkedResults={linkedResults}
			isMentionedInEnabled={isMentionedInEnabled}
			is3pGroupingEnabled={is3pGroupingEnabled}
			isAllProductSelected={!selectedProduct}
			permissionLevel={toThirdPartyRestrictionLevel(permissionLevel)}
			isNotPublic={isNotPublic}
			onResultClicked={onResultClickedWithThirdPartyId}
			onResultContextMenu={onResultContextMenuWithThirdPartyId}
			position={position}
			id={id}
			url={url}
			enrichedTitle={enrichedTitle}
			product={product}
			selectedProduct={selectedProduct}
			primaryProduct={primaryProduct}
			thirdPartyConfigs={thirdPartyConfigs}
			partial={data}
		/>
	);

	const searchResult = (
		<SearchResult
			{...data}
			id={id}
			product={product}
			productSelected={!!selectedProduct}
			primaryProduct={primaryProduct}
			primarySubProduct={primarySubProduct}
			onResultClicked={onResultClickedWithThirdPartyId}
			onResultContextMenu={onResultContextMenuWithThirdPartyId}
			onCopyLinkButtonClicked={onCopyLinkButtonClicked}
			onSummarizeInChatButtonClicked={onSummarizeInChatButtonClicked}
			onContextMenuCopyButtonClicked={onContextMenuCopyButtonClicked}
			onContextMenuButtonClicked={onContextMenuButtonClicked}
			url={url}
			title={enrichedTitle}
			icon={documentIcon}
			headerMetadataParts={headerMetadataParts}
			headerMetadataSocialParts={headerMetadataSocialParts}
			description={description}
			cloudId={cloudId}
			position={position}
			partial={data}
			appMode={appMode}
			isThirdParty
			footer={footerComponent}
			hasRelatedLinks={!!linkedResults && linkedResults.length > 0}
			mainContentWidth={mainContentWidth}
			selectedSites={selectedSites}
		/>
	);

	// When a status is present (e.g. Salesforce knowledge article status), render it in the
	// top-right column alongside the result.
	if (statusText) {
		return (
			<Flex gap="space.100" alignItems="start" role="listitem" xcss={styles.documentContainer}>
				<Box xcss={styles.resultWrapper}>{searchResult}</Box>
				<StatusLozengeButton statusText={statusText} />
			</Flex>
		);
	}

	return searchResult;
};
